export type ErrorCategory = "timeout" | "warehouse" | "generic";
export interface CategorizedError {
  category: ErrorCategory;
  title: string;
  message: string;
}

/**
 * Classify a raw error string into a user-facing category + humanized copy.
 *
 * Regex alignment with real error strings:
 * - timeout: ClickHouse max_execution_time=60 in clickhouse.ts; CH throws
 *   "DB::Exception: ... max_execution_time exceeded" or similar.
 * - warehouse: ClickHouse DB::Exception prefix covers syntax/unknown column
 *   errors; "refusing to run" covers the SETTINGS-clause guard in clickhouse.ts.
 */
export function categorizeError(raw: string | null | undefined): CategorizedError {
  const e = (raw ?? "").toLowerCase();

  if (/timeout|timed out|max_execution_time|timeout_exceeded|query exceeded/.test(e)) {
    return {
      category: "timeout",
      title: "Query timed out",
      message: "That query took too long to run. Try narrowing the time window or asking something more specific.",
    };
  }

  if (
    /db::exception|clickhouse|syntax|unknown (column|identifier|table)|cannot_read|cannot parse|refusing to run/.test(e)
  ) {
    return {
      category: "warehouse",
      title: "Couldn't run that query",
      message: "We hit a problem querying the warehouse. Rephrasing the question often helps.",
    };
  }

  return {
    category: "generic",
    title: "Something went wrong",
    message: "That didn't work. You can try again, or rephrase the question.",
  };
}
